import { useCallback, useState } from 'react';
import storeLocationsService from '../services/storeLocationsService';
import type { StoreLocationType } from '../../../../types/features/storeDetails/locations';
import showToast from '../../../../utils/toastUtils';
import {
  DEFAULT_VENDING_BIN_COUNT,
  parseVendingBinCount,
  VENDING_BIN_COUNT_ERROR,
} from '../utils/vendingBinCount';

type UseAddLocationDialogOptions = {
  storeId: string;
  onCreated?: () => void | Promise<void>;
  onRegistersRefreshed?: () => void | Promise<void>;
};

export const useAddLocationDialog = ({ storeId, onCreated, onRegistersRefreshed }: UseAddLocationDialogOptions) => {
  const [open, setOpen] = useState(false);
  const [locationKey, setLocationKey] = useState('');
  const [locationName, setLocationName] = useState('');
  const [locationType, setLocationType] = useState<StoreLocationType | ''>('');
  const [vendingBinCount, setVendingBinCount] = useState(String(DEFAULT_VENDING_BIN_COUNT));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const resetForm = useCallback(() => {
    setLocationKey('');
    setLocationName('');
    setLocationType('');
    setVendingBinCount(String(DEFAULT_VENDING_BIN_COUNT));
    setError('');
  }, []);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    if (submitting) {
      return;
    }

    setOpen(false);
    resetForm();
  }, [resetForm, submitting]);

  const handleSubmit = useCallback(async () => {
    if (!storeId) {
      setError('Store is not available.');
      return;
    }

    if (!locationKey.trim() || !locationName.trim() || !locationType) {
      setError('Please fill in all fields.');
      return;
    }

    const parsedVendingBinCount = parseVendingBinCount(vendingBinCount);
    if (locationType === 'vending' && parsedVendingBinCount === null) {
      setError(VENDING_BIN_COUNT_ERROR);
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await storeLocationsService.createStoreLocation(storeId, {
        location_key: locationKey.trim(),
        location_name: locationName.trim(),
        location_type: locationType,
        ...(locationType === 'vending' && parsedVendingBinCount !== null
          ? { vending_bin_count: parsedVendingBinCount }
          : {}),
      });
      showToast.success('Location added successfully');
      setOpen(false);
      resetForm();
      onCreated?.();
      await onRegistersRefreshed?.();
    } catch (submitError) {
      const message = submitError instanceof Error ? submitError.message : 'Failed to add location.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }, [locationKey, locationName, locationType, onCreated, onRegistersRefreshed, resetForm, storeId, vendingBinCount]);

  return {
    open,
    error,
    locationKey,
    locationName,
    locationType,
    vendingBinCount,
    submitting,
    handleClose,
    handleOpen,
    handleSubmit,
    setLocationKey,
    setLocationName,
    setLocationType,
    setVendingBinCount,
  };
};
